import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet, View } from "react-native";
import type { UiTheme } from "../theme";
import { Segment, type SegmentIcon } from "./Segment";
import {
  AdminSegmentIcon,
  AthleteSegmentIcon,
  CoachSegmentIcon,
  SettingsSegmentIcon,
} from "./segmentIcons";

/** The three role lenses; each host's own `Experience` union is this one. */
export type ExperienceId = "athlete" | "coach" | "admin";

export type ExperienceSwitcherProps = {
  theme: UiTheme;
  experiences: ExperienceId[];
  active: ExperienceId;
  onSelect: (e: ExperienceId) => void;
  /** ⚙︎ is the segment on screen, no experience is. */
  settingsActive: boolean;
  onOpenSettings: () => void;
  /** Last segment of the row (INBOX-10: the bell); it brings its own Segment. */
  trailing?: ReactNode;
  /** Caps the row's width and keeps it at the start edge (a wide viewport,
   *  where five flex:1 segments would otherwise stretch across the screen). */
  rowMaxWidth?: number;
};

const ICONS: Record<ExperienceId, SegmentIcon> = {
  athlete: AthleteSegmentIcon,
  coach: CoachSegmentIcon,
  admin: AdminSegmentIcon,
};

// Top experience selector. Styled like the BOTTOM tab bar (icon over small
// label, active = brand tint), the header tells you WHICH mode, the footer
// tells you WHERE within it (Antenore's model). Mobile's top safe-area inset is
// ShellChrome's (INBOX-10), which pads the whole column above the navigator.
//
// ME-1 put Settings here as the ⚙︎ segment, inline beside the experiences: it is
// app config, "a mode even though it isn't properly one", flat, no new tier, no
// title of its own. Which means this can no longer render nothing for single-role
// users (the family): with Settings out of the bottom bar the gear is the only
// door to it.
//
// So a single-role user gets "role chip + ⚙︎" (two segments), NOT a lone gear,
// the spec left that open and Antenore picked the chip. It isn't decoration: the
// bottom bar is hidden while Settings is up, so that chip is the ONLY way back
// out. A lone gear would strand the family in Settings. The cost, accepted
// knowingly: the chip is inert while you're already in your experience.
//
// Moved from apps/mobile to @heraion/ui (#208) so the web shell mounts the same
// row; the host passes the theme, as every other lifted component does.
export function ExperienceSwitcher({
  theme,
  experiences,
  active,
  onSelect,
  settingsActive,
  onOpenSettings,
  trailing,
  rowMaxWidth,
}: ExperienceSwitcherProps) {
  const { palette: p } = theme;
  const { t } = useTranslation();

  return (
    <View
      style={{
        backgroundColor: p.surface,
        borderBottomWidth: 1,
        borderBottomColor: p.border,
      }}
    >
      <View
        style={[
          styles.row,
          rowMaxWidth != null && { maxWidth: rowMaxWidth, width: "100%", alignSelf: "flex-start" },
        ]}
      >
        {/* The roles and the gear are the tabs; the bell is an action, so it
            stays outside the tablist (same split as Sidebar and TabBar). The
            tablist takes one flex unit per tab so widths match a flat row. */}
        <View
          accessibilityRole="tablist"
          style={[styles.row, { flex: experiences.length + 1 }]}
        >
          {/* ⚙︎ is a segment like the experiences (ME-1), so it joins the same
              list and renders through the same map, one <Segment> callsite, not
              a fourth hand-written copy beside it (ME-1b). */}
          {[
            ...experiences.map((e) => ({
              key: e,
              // An experience is only "the segment on screen" when ⚙︎ isn't.
              selected: e === active && !settingsActive,
              icon: ICONS[e],
              label: t(`experience.${e}`),
              onPress: () => onSelect(e),
            })),
            {
              key: "settings",
              selected: settingsActive,
              icon: SettingsSegmentIcon,
              label: t("mobile.tabs.settings", { defaultValue: "Settings" }),
              onPress: onOpenSettings,
            },
          ].map((s) => (
            <Segment
              key={s.key}
              selected={s.selected}
              icon={s.icon}
              label={s.label}
              onPress={s.onPress}
              palette={p}
            />
          ))}
        </View>
        {trailing}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  row: { flexDirection: "row" },
});
